import { useSuspenseQuery } from '@apollo/client';
import { CopyPlusIcon } from 'lucide-react';
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { TooltipLabel } from '@eluve/blocks';
import {
  Box,
  Button,
  ColDefBuilder,
  DataTable,
  HStack,
  LinkButton,
  PageTitle,
} from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';
import { useIsEluveAdmin } from '@eluve/session-helpers';

import { ToggleOutputTemplateActiveAction } from './ToggleOutputTemplateActiveAction';

export interface OutputTemplatesListPageProps {}

const getOutputTemplatesQuery = graphql(`
  query getOutputTemplates {
    llmOutputTemplates(where: { tenantId: { _isNull: true } }) {
      __typename
      id
      name
      description
      llmOutputType
      isActive
      createdAt
      variantsAggregate {
        aggregate {
          count
        }
      }
    }
  }
`);

type OutputTemplateRow = {
  id: string;
  name: string;
  description: string;
  outputType: string;
  isActive: boolean;
  createdAt: string;
  variantCount: number;
};

const createColumns = (_isEluveAdmin: boolean) => {
  return new ColDefBuilder<OutputTemplateRow>()
    .linkSortable('name', (row) => `./${row.id}`)
    .defaultSortable('description')
    .defaultSortable('outputType', 'Output Type')
    .defaultBoolean('isActive', 'Active')
    .dateSortable('createdAt', 'Created At')
    .defaultSortable('variantCount', '# Variant')
    .colDef({
      header: 'Actions',
      cell: ({ row }) => (
        <Box hStack gap={1}>
          <TooltipLabel
            label={`Create a new Output Template using '${row.original.name}' as a starting point`}
          >
            <Link to={`./clone/${row.original.id}`}>
              <Button size="icon" variant="outline">
                <CopyPlusIcon />
              </Button>
            </Link>
          </TooltipLabel>
          <ToggleOutputTemplateActiveAction
            outputTemplateId={row.original.id}
          />
        </Box>
      ),
    })
    .build();
};

export const OutputTemplatesListPage: React.FC<
  OutputTemplatesListPageProps
> = () => {
  const isEluveAdmin = useIsEluveAdmin();
  const {
    data: { llmOutputTemplates },
  } = useSuspenseQuery(getOutputTemplatesQuery);

  const columns = useMemo(() => createColumns(isEluveAdmin), [isEluveAdmin]);

  const rows = useMemo(() => {
    return llmOutputTemplates.map<OutputTemplateRow>(
      ({
        id,
        isActive,
        name,
        description,
        llmOutputType,
        createdAt,
        variantsAggregate,
      }) => ({
        id,
        name,
        description: description ?? '',
        outputType: llmOutputType,
        isActive,
        createdAt,
        variantCount: variantsAggregate?.aggregate?.count ?? 0,
      }),
    );
  }, [llmOutputTemplates]);

  return (
    <>
      <HStack justify="between">
        <div className="flex-1">
          <PageTitle>Output Templates</PageTitle>
        </div>
        <LinkButton
          to="create"
          text="Create an Output Template"
          icon={{
            name: 'Plus',
          }}
        />
      </HStack>
      <div>
        <DataTable
          columns={columns}
          data={rows}
          enableGlobalSearch
          initialSortingState={[{ id: 'createdAt', desc: true }]}
        />
      </div>
    </>
  );
};
